import { AloftDate, FormType, ReportJSON, isDefined } from '@aloft/toolkit-shared';

import {
  COST_APPROACH_SITE_COMMENT,
  getCostApproachReplacementCostComment,
} from '../../../summaryV2/CostApproachSummary/consts.ts';
import { scalarToNumberToString } from '../../utils.ts';
// eslint-disable-next-line aloft/prefer-sound-ui-box
import { createCheckBoxFieldNode } from '../createCheckBoxFieldNode.ts';
import { createTextFieldNode } from '../createTextFieldNode.ts';
import { generateOrderInputRptiNodes } from '../generateOrderInputRptiNodes.ts';
import { generateTkv2AdjustmentsRptiNodes } from '../generateTkv2AdjustmentsRptiNodes.ts';
import { generateTkv2UADAnnotationRptiNodes } from '../generateTkv2UADAnnotationRptiNodes.ts';

export function create1004cFormNode(
  doc: XMLDocument,
  effectiveDate: AloftDate | undefined,
  compSequence: string[],
  { comps, subject, meta }: ReportJSON,
): HTMLElement {
  const fnma1004 = doc.createElement('Form');
  fnma1004.setAttribute('Id', 'FNMA-1004C-0305');

  const reportFields = doc.createElement('Fields');
  fnma1004.append(reportFields);

  if (isDefined(effectiveDate)) {
    reportFields.append(...generateOrderInputRptiNodes(doc, effectiveDate));
  }
  reportFields.append(
    ...generateTkv2UADAnnotationRptiNodes(doc, {
      property: subject.property,
      isComp: false,
      meta,
    }),
  );

  if (compSequence.length > 0) {
    compSequence.forEach((compId, index) => {
      const comp = comps[compId];

      if (comp) {
        reportFields.append(
          ...generateTkv2UADAnnotationRptiNodes(doc, {
            property: comp,
            isComp: true,
            meta,
            compIndex: index,
          }),
        );
      }
    });
  }

  // Add Cost Approach RPTI nodes from Subject Cost Results
  reportFields.append(...generate1004cCostDataRptiNodes(doc, subject.costApproach));

  // Add Adjustments each comp
  compSequence.forEach((compId, index) => {
    const comp = comps?.[compId];

    if (comp) {
      reportFields.append(
        ...generateTkv2AdjustmentsRptiNodes(doc, comp, index, FormType.fnma1004c, meta),
      );
    }
  });

  fnma1004.appendChild(reportFields);

  return fnma1004;
}

function generate1004cCostDataRptiNodes(
  doc: XMLDocument,
  costApproach: ReportJSON['subject']['costApproach'],
): HTMLElement[] {
  const rptiNodes = [];

  const { remainingEcoLife, effectiveAge } = costApproach ?? {};

  if (isDefined(costApproach)) {
    rptiNodes.push(
      createCheckBoxFieldNode(doc, {
        Id: 'EstimatedReplacementCostNewCheckBox',
        Data: true,
      }),
    );

    // Cost Approach
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostDataSources',
        Data: 'DwellingCost.com',
      }),
    );

    // cost data effective date
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostDataEffectiveDate',
        Data: costApproach.source?.effectiveDate.format('MM/dd/yyyy'),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostServiceQualityRating',
        Data: costApproach.dwellingCostQuality,
      }),
    );

    // Estimated remaining economic life
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EstimatedRemainingEconomicLife',
        Data: `${remainingEcoLife ?? ''}`,
      }),
    );

    // effective age
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EffectiveAge',
        Data: `${effectiveAge ?? ''}`,
      }),
    );

    // opinion of site value
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'OpinionOfSiteValueAmount',
        Data: scalarToNumberToString(costApproach.site),
      }),
    );

    // gross living area
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueSectionOneSqFt',
        Data: scalarToNumberToString(costApproach.dwelling?.sqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueSectionOnePricePerSqFtAmount',
        Data: scalarToNumberToString(costApproach.dwelling?.costPerSqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueSectionOneTotalValueAmount',
        Data: scalarToNumberToString(costApproach.dwelling?.totalCost),
      }),
    );

    const numSections = costApproach.sections ?? 1;
    if (numSections > 1) {
      rptiNodes.push(
        createTextFieldNode(doc, {
          Id: 'SiteValueSectionTwoPricePerSqFtAmount',
          Data: scalarToNumberToString(costApproach.dwelling?.costPerSqft),
        }),
      );
    }
    if (numSections > 2) {
      rptiNodes.push(
        createTextFieldNode(doc, {
          Id: 'SiteValueSectionThreePricePerSqFtAmount',
          Data: scalarToNumberToString(costApproach.dwelling?.costPerSqft),
        }),
      );
    }
    if (numSections > 3) {
      rptiNodes.push(
        createTextFieldNode(doc, {
          Id: 'SiteValueSectionFourPricePerSqFtAmount',
          Data: scalarToNumberToString(costApproach.dwelling?.costPerSqft),
        }),
      );
    }

    // Basement
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankLabel',
        Data: costApproach.basement ? 'Basement' : '',
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankSqFt',
        Data: scalarToNumberToString(costApproach.basement?.sqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankPricePerSqFtAmount',
        Data: scalarToNumberToString(costApproach.basement?.costPerSqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankAmount',
        Data: scalarToNumberToString(costApproach.basement?.totalCost),
      }),
    );

    // Additional Structures
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlank2Label',
        Data: costApproach?.additionalStructures ? 'Add. Struct. & Feats' : '',
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlank2Amount',
        Data: scalarToNumberToString(costApproach.additionalStructures?.totalCost),
      }),
    );

    // Car Storage
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlank3Label',
        Data: costApproach?.garageCarport ? 'Garage/Carport' : '',
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlank3Amount',
        Data: scalarToNumberToString(costApproach.garageCarport?.totalCost),
      }),
    );

    // TODO: Estimated Cost New Total Description
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EstimatedCostNewTotalDescription',
      }),
    );

    // Depreciation
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationPhysicalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.physical?.abs()),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationFunctionalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.functional?.abs()),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationExternalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.external?.abs()),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationTotalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.total.abs()),
      }),
    );

    // Indicate Value
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'IndicatedValueCostApproachAmount',
        Data: scalarToNumberToString(costApproach?.indicatedValue),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostApproachComments',
        Data: getCostApproachReplacementCostComment(costApproach.source?.name ?? 'Craftsman'),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'OpinionOfSiteValueSupport',
        Data: COST_APPROACH_SITE_COMMENT,
      }),
    );
  }

  if (costApproach?.depreciated) {
    // Improvement Depreciation
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'ImprovementsDepreciatedCostAmount',
        Data: scalarToNumberToString(costApproach.depreciated.cost),
      }),
    );
  }
  if (costApproach?.new) {
    // Estimated cost New Total Amount
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EstimatedCostNewTotalAmount',
        Data: scalarToNumberToString(costApproach.new.cost),
      }),
    );
  }
  return rptiNodes;
}
